import React, { useState, useEffect } from 'react';
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, CircularProgress, Alert, Chip } from '@mui/material';
import CommonModal from '../../../components/common/CommonModal';
import UserFormModal from '../../../components/Modals/UserFormModal';
import { useUsers } from '../../../hooks/useUsers';
import { UserData } from '../../../services/userService';
import { useTranslation } from "react-i18next";
import { useError } from '../../../contexts/ErrorContext';
import EnhancedTable from '../../../components/common/EnhancedTable';
import ActionButtons from '../../../components/common/ActionButtons';
import { useQueryClient } from '@tanstack/react-query';

const UserManagement: React.FC = () => {
  const {
    usersQuery,
    createUserMutation,
    updateUserMutation,
    deleteUserMutation,
  } = useUsers('SCHOOL_ADMIN'); // Use role-based API for SCHOOL_ADMIN
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { showSuccess, showError } = useError();

  // Handle mutation states
  useEffect(() => {
    if (deleteUserMutation.isSuccess) {
      showSuccess('School admin deleted successfully');
      deleteUserMutation.reset();
    }
    if (deleteUserMutation.isError) {
      const error = deleteUserMutation.error as any;
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || '';
      showError(errorMessage || 'Failed to delete user. Please try again.');
      deleteUserMutation.reset();
    }
  }, [deleteUserMutation.isSuccess, deleteUserMutation.isError, showSuccess, showError]);

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserData | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);



  const handleOpenAddModal = () => {
    setIsEdit(false);
    setCurrentUser(undefined);
    setModalOpen(true);
  };

  const handleOpenEditModal = (user: UserData) => {
    setIsEdit(true);
    // Clean the user data before passing to modal - remove 'role' field if it exists
    const { role, ...cleanUser } = user as any;
    setCurrentUser(cleanUser as UserData);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleSubmit = (formData: UserData) => {
    if (isEdit && currentUser?.id) {
      updateUserMutation.mutate(
        { id: currentUser.id, data: formData },
        {
          onSuccess: () => {
            showSuccess(t('user_updated_successfully') || 'User updated successfully');
            handleCloseModal();
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || '';
            const errorLower = errorMessage.toLowerCase();

            // Check for duplicate email error - handle various formats
            if (errorLower.includes('email') &&
              (errorLower.includes('already') ||
                errorLower.includes('exist') ||
                errorLower.includes('duplicate') ||
                errorLower.includes('taken'))) {
              showError(t('email_already_exists') || 'User with this email already exists');
            } else if (errorMessage) {
              showError(errorMessage);
            } else {
              showError(t('error_updating_user') || 'Failed to update user. Please try again.');
            }
          },
        }
      );
    } else {
      createUserMutation.mutate(formData, {
        onSuccess: () => {
          showSuccess(t('user_created_successfully') || 'User created successfully');
          handleCloseModal();
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || '';
          const errorLower = errorMessage.toLowerCase();

          // Check for duplicate email error - handle various formats
          if (errorLower.includes('email') &&
            (errorLower.includes('already') ||
              errorLower.includes('exist') ||
              errorLower.includes('duplicate') ||
              errorLower.includes('taken'))) {
            showError(t('email_already_exists') || 'User with this email already exists');
          } else if (errorMessage) {
            showError(errorMessage);
          } else {
            showError(t('error_creating_user') || 'Failed to create user. Please try again.');
          }
        },
      });
    }
  };

  const handleOpenDeleteDialog = (id: number) => {
    setUserToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (userToDelete !== null) {
      deleteUserMutation.mutate(userToDelete);
      handleCloseDeleteDialog();
    }
  };



  const columns = [
    { id: 'username', label: t('username'), minWidth: 130 },
    { id: 'email', label: t('email'), minWidth: 180 },
    { id: 'status', label: t('status'), minWidth: 100 },
  ];

  const formattedData = (usersQuery.data || [])
    .filter(user =>
      user.roles.includes('SCHOOL_ADMIN')
      // Show all users regardless of status (active and inactive)
    )
    .map((row: any) => ({
      ...row,
      status: row.status ? "Active" : "Inactive",
    }));

  const renderActions = (row: any) => {
    if (row.roles?.includes('STUDENT') || row.roles?.includes('TEACHER')) {
      return null;
    }
    return (
      <ActionButtons
        onEdit={() => handleOpenEditModal(row)}
        onDelete={() => handleOpenDeleteDialog(row.id)}
        editTooltip={t('edit')}
        deleteTooltip={t('delete') || 'Delete'}
      />
    );
  };

  return (
    <>
      <Paper sx={{ p: 2, width: '100%', maxWidth: 1500, mx: 'auto', mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            {t('user_management')}
          </Typography>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            onClick={handleOpenAddModal}
          >
            {t('add_user')}
          </Button>
        </Box>

        {usersQuery.isLoading && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}
        {usersQuery.error instanceof Error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {usersQuery.error.message}
          </Alert>
        )}

        <EnhancedTable
          columns={columns}
          data={formattedData}
          searchFields={['username', 'email', 'role', 'status']}
          filterFields={[]}
          cardProps={{
            elevation: 0,
            sx: {
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent'
            }
          }}
          actions={renderActions}
        />
      </Paper>

      <UserFormModal
        open={modalOpen}
        handleClose={handleCloseModal}
        initialData={currentUser}
        onSubmit={handleSubmit}
        isEdit={isEdit}
      />

      <CommonModal
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        title={t('confirm_delete') || 'Confirm Delete'}
        maxWidth="xs"
        customActions={
          <>
            <Button
              onClick={handleCloseDeleteDialog}
              variant="outlined"
              disabled={deleteUserMutation.isLoading}
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              disabled={deleteUserMutation.isLoading}
            >
              {deleteUserMutation.isLoading ? (
                <><CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />{t('deleting') || 'Deleting...'}</>
              ) : (
                t('delete') || 'Delete'
              )}
            </Button>
          </>
        }
      >
        <Typography color="text.secondary">
          {t('confirm_delete_user') || 'Are you sure you want to delete this school admin? They will no longer be able to access the system.'}
        </Typography>
      </CommonModal>
    </>
  );
};

export default UserManagement;